// src/utils/ticketDisplay.ts
//
// The Epic-4 ticket-desk display vocabulary — the SINGLE source of truth for
// how a ticket's status, route, flags, and question type render in the console.
// Every later ticket screen (4.2 detail, 4.8 failure view, 4.9 bell) imports
// these; none redefines the casing or hues. DB values are lowercase (3.5
// Decision 2); the display casing lives ONLY here.
//
// Why a helper and not inline JSX: the mappings are pure data and must be unit-
// tested exhaustively (every status/route/flag/type has one deterministic
// rendering), and the vocabulary must stay identical across the whole epic.

import type {
  YakTicketStatus,
  YakTicketRoute,
  YakTicketWithAssignee,
  YakTicket,
  YakAssignmentReason,
  YakMessageKind,
} from '../types/yak'

// Status badge = a FILLED hue-100/hue-800 badge (Badge component variants),
// always carrying its text label. OPEN blue / ANSWERED yellow / CLOSED gray.
export interface TicketStatusBadge {
  label: 'OPEN' | 'ANSWERED' | 'CLOSED'
  variant: 'blue' | 'yellow' | 'gray'
}

export function ticketStatusBadge(status: YakTicketStatus): TicketStatusBadge {
  switch (status) {
    case 'open':
      return { label: 'OPEN', variant: 'blue' }
    case 'answered':
      return { label: 'ANSWERED', variant: 'yellow' }
    case 'closed':
      return { label: 'CLOSED', variant: 'gray' }
  }
}

// Route chip = an OUTLINED pill (border + hue text), deliberately quieter than
// the status badge — it is provenance metadata, not a call to action (DESIGN.md).
// AUTO green / REVIEW neutral gray. Exactly two arms: route_label ∈ auto|review
// (the legacy 'escalate' does not exist in the rebuild).
export interface TicketRouteChip {
  label: 'AUTO' | 'REVIEW'
  tone: 'green' | 'gray'
}

export function routeChip(route: YakTicketRoute): TicketRouteChip {
  return route === 'auto'
    ? { label: 'AUTO', tone: 'green' }
    : { label: 'REVIEW', tone: 'gray' }
}

// Row-level flag chips (red-100/red-800 badge form; label always present so
// color is never the sole carrier). Ordered: unroutable first, then AE. Only
// these two flags get a row chip — extraction_failed reads as itself via its
// authored question text, and wildcard_attributes belongs to 4.5's fields panel.
export interface TicketFlagChip {
  key: 'unroutable' | 'ae_flag'
  label: 'Unroutable' | 'AE'
  tone: 'red'
}

export function ticketFlags(
  ticket: Pick<YakTicket, 'unroutable' | 'ae_flag'>
): TicketFlagChip[] {
  const flags: TicketFlagChip[] = []
  if (ticket.unroutable) {
    flags.push({ key: 'unroutable', label: 'Unroutable', tone: 'red' })
  }
  if (ticket.ae_flag) {
    flags.push({ key: 'ae_flag', label: 'AE', tone: 'red' })
  }
  return flags
}

// The five canonical question-type display labels — byte-identical to Story
// 3.7's send-outbound composer map (QUESTION_TYPE_LABELS), so email threads and
// the console speak one vocabulary. Unknown/absent keys degrade to "Other".
export const TICKET_QUESTION_TYPE_LABELS: Record<string, string> = {
  formulary_status: 'Formulary status',
  rejected_rx: 'Rejected Rx / prior auth',
  out_of_pocket: 'Out-of-pocket / copay',
  pharmacy_wholesaler: 'Pharmacy & wholesaler',
  other: 'Other',
}

export function questionTypeLabel(type: string): string {
  return TICKET_QUESTION_TYPE_LABELS[type] ?? 'Other'
}

// The brand top-level string inside AD-14's canonical structured_fields jsonb.
// Renders as an em dash when absent (the row spec: "brand (— when absent)").
export function ticketBrand(ticket: YakTicketWithAssignee): string {
  const brand = ticket.structured_fields?.brand
  return typeof brand === 'string' && brand.trim() ? brand : '—'
}

// Assignee display name: "First Last", falling back to whichever name part is
// present, then the email. Deactivated assignees still display by name — the
// row states a fact, no liveness check (mirrors 3.7 Decision 13).
export function assigneeName(ticket: YakTicketWithAssignee): string {
  const a = ticket.assignee
  if (!a) return '—'
  const full = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return full || a.email || '—'
}

// General person-name helper for the detail view's assignment timeline and
// author attributions (name parts + email fallback). Mirrors assigneeName but
// takes the raw name fields so it works for any embed shape.
export function personName(person: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}): string {
  const full = [person.first_name, person.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return full || person.email || '—'
}

// Assignment-reason display labels (3.5's full vocabulary, mapped ONCE here so
// the timeline and 4.4's future escalation notes speak one vocabulary). Only
// `initial` rows exist until 4.4/4.6 ship; the rest are fixed now.
export const ASSIGNMENT_REASON_LABELS: Record<YakAssignmentReason, string> = {
  initial: 'Assigned by rule',
  escalated: 'Escalated',
  manual: 'Reassigned manually',
  deactivation: 'Reassigned after deactivation',
  reopen: 'Reopened',
}

export function assignmentReasonLabel(reason: YakAssignmentReason): string {
  return ASSIGNMENT_REASON_LABELS[reason] ?? 'Reassigned'
}

// Draft-panel label is ROUTE-DEPENDENT (3.6 Decision 6): a REVIEW draft is an
// unverified draft; an AUTO draft is the delivered answer AS SPOKEN on the call
// — never "pending review". This label exists to stop "Yak's draft" from
// implying an un-reviewed AUTO answer (the story's headline trap).
export function draftPanelLabel(route: YakTicketRoute): string {
  return route === 'auto'
    ? "Yak's answer — as spoken on the call"
    : "Yak's draft"
}

// Thread message-kind rendering metadata. `internal_note` is visually walled
// (yellow) and labelled so it can NEVER be confused with a rep-visible reply.
export interface MessageKindMeta {
  // Human label shown on the message (null = no explicit banner label).
  label: string | null
  // Visual tier the ThreadList renders.
  tone: 'surface' | 'internal' | 'rep'
}

export function messageKindMeta(kind: YakMessageKind): MessageKindMeta {
  switch (kind) {
    case 'response':
      return { label: null, tone: 'surface' }
    case 'internal_note':
      return { label: 'Internal — not visible to rep', tone: 'internal' }
    case 'rep_reply':
      return { label: null, tone: 'rep' }
  }
}
